import { useEffect } from 'react';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

interface UseInboxCompositionProps {
  filteredCommunications: UserCommunication[];
  onOpenCreateNewUserCommunication: () => void;
  onSelectReply?: (userCommunication: UserCommunication) => void;
  onSelectReplyAll?: (userCommunication: UserCommunication) => void;
  onSelectForward?: (userCommunication: UserCommunication) => void;
}

export function useInboxComposition({
  filteredCommunications,
  onOpenCreateNewUserCommunication,
  onSelectReply,
  onSelectReplyAll,
  onSelectForward,
}: UseInboxCompositionProps) {
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts({
    disableFocusSteal: true,
  });
  const { selectedEmailIndex } = useShortcutsContext();

  useEffect(() => {
    registerShortcut({
      key: ShortcutKey.COMPOSE,
      contexts: [
        ShortcutContext.INBOX_LIST,
        ShortcutContext.ALL_MAIL,
        ShortcutContext.SENT,
        ShortcutContext.SPAM,
        ShortcutContext.DRAFT,
        ShortcutContext.SEARCH,
        ShortcutContext.DOBOX,
        ShortcutContext.GLOBAL,
      ],
      handler: () => {
        onOpenCreateNewUserCommunication();
      },
      description: 'Compose new email',
    });

    if (onSelectReply) {
      registerShortcut({
        key: ShortcutKey.REPLY,
        contexts: [ShortcutContext.INBOX_LIST],
        handler: () => {
          if (
            selectedEmailIndex >= 0 &&
            selectedEmailIndex < filteredCommunications.length
          ) {
            const selectedEmail = filteredCommunications[selectedEmailIndex];
            if (selectedEmail) {
              onSelectReply(selectedEmail);
            }
          }
        },
        description: 'Reply to current email',
      });
    }

    if (onSelectReplyAll) {
      registerShortcut({
        key: ShortcutKey.REPLY_ALL,
        contexts: [ShortcutContext.INBOX_LIST],
        handler: () => {
          if (
            selectedEmailIndex >= 0 &&
            selectedEmailIndex < filteredCommunications.length
          ) {
            const selectedEmail = filteredCommunications[selectedEmailIndex];
            if (selectedEmail) {
              onSelectReplyAll(selectedEmail);
            }
          }
        },
        description: 'Reply to all',
      });
    }

    if (onSelectForward) {
      registerShortcut({
        key: ShortcutKey.FORWARD,
        contexts: [ShortcutContext.INBOX_LIST],
        handler: () => {
          if (
            selectedEmailIndex >= 0 &&
            selectedEmailIndex < filteredCommunications.length
          ) {
            const selectedEmail = filteredCommunications[selectedEmailIndex];
            if (selectedEmail) {
              onSelectForward(selectedEmail);
            }
          }
        },
        description: 'Forward current email',
      });
    }

    const composeCtx = [
      ShortcutContext.INBOX_LIST,
      ShortcutContext.ALL_MAIL,
      ShortcutContext.SENT,
      ShortcutContext.SPAM,
      ShortcutContext.DRAFT,
      ShortcutContext.SEARCH,
      ShortcutContext.DOBOX,
      ShortcutContext.GLOBAL,
    ];
    const listCtx = [ShortcutContext.INBOX_LIST];
    return () => {
      unregisterShortcut(ShortcutKey.COMPOSE, composeCtx);
      if (onSelectReply) {
        unregisterShortcut(ShortcutKey.REPLY, listCtx);
      }
      if (onSelectReplyAll) {
        unregisterShortcut(ShortcutKey.REPLY_ALL, listCtx);
      }
      if (onSelectForward) {
        unregisterShortcut(ShortcutKey.FORWARD, listCtx);
      }
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    filteredCommunications,
    onOpenCreateNewUserCommunication,
    onSelectReply,
    onSelectReplyAll,
    onSelectForward,
  ]);
}
